// Transform run history list — ontology spec §7.2.
// Shows the last 20 transform runs for a transform, with status badges and timing.
// Polls every 5s while any run is 'running' (via useTransformRuns).
// Clicking a run expands to show rows_failed detail.

import { useState } from "react";
import { CheckCircle, XCircle, Loader2, Clock } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useTransformRuns } from "@/hooks/useTransformRuns";
import type { TransformRun, TransformRunStatus } from "@/types/ontology";

interface RunHistoryListProps {
  transformId: string;
}

export function RunHistoryList({ transformId }: RunHistoryListProps) {
  const { runs, isLoading } = useTransformRuns(transformId);
  const [expandedRunId, setExpandedRunId] = useState<string | null>(null);

  if (isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-12 rounded-md" />
        ))}
      </div>
    );
  }

  if (runs.length === 0) {
    return <p className="text-xs text-muted-foreground py-2">No runs yet.</p>;
  }

  const displayRuns = runs.slice(0, 20);

  return (
    <div className="space-y-2" data-testid="run-history-list">
      {displayRuns.map((run) => (
        <RunRow
          key={run.id}
          run={run}
          isExpanded={expandedRunId === run.id}
          onToggle={() => setExpandedRunId(expandedRunId === run.id ? null : run.id)}
        />
      ))}
    </div>
  );
}

interface RunRowProps {
  run: TransformRun;
  isExpanded: boolean;
  onToggle: () => void;
}

function RunRow({ run, isExpanded, onToggle }: RunRowProps) {
  const durationMs =
    run.finished_at && run.started_at
      ? new Date(run.finished_at).getTime() - new Date(run.started_at).getTime()
      : null;

  return (
    <div className="rounded-md border border-border text-xs">
      <button
        className="w-full flex items-center gap-3 px-3 py-2 hover:bg-muted/40 transition-colors text-left"
        onClick={onToggle}
      >
        <StatusIcon status={run.status} />
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <StatusBadge status={run.status} />
            <span className="text-muted-foreground capitalize">
              {run.trigger.replace("_", " ")}
            </span>
            {durationMs !== null && (
              <span className="text-muted-foreground flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {durationMs < 1000 ? `${durationMs}ms` : `${(durationMs / 1000).toFixed(1)}s`}
              </span>
            )}
          </div>
          <div className="text-muted-foreground mt-0.5">
            {run.rows_in} in · {run.rows_out} out
            {run.rows_failed > 0 && (
              <span className="text-destructive ml-1">· {run.rows_failed} failed</span>
            )}
            <span className="ml-2">{formatRelative(run.started_at)}</span>
          </div>
        </div>
        <span className="text-muted-foreground shrink-0">{isExpanded ? "▲" : "▼"}</span>
      </button>

      {isExpanded && (
        <div className="border-t border-border px-3 py-2 space-y-1">
          {run.error ? (
            <div>
              <p className="font-medium text-destructive mb-1">Error</p>
              <pre className="text-xs bg-muted/40 rounded p-2 overflow-x-auto max-h-40 whitespace-pre-wrap">
                {formatError(run.error)}
              </pre>
            </div>
          ) : (
            <p className="text-muted-foreground">No errors.</p>
          )}
          {run.upstream_materialization_id && (
            <p className="text-muted-foreground text-xs">
              Triggered by materialization: {run.upstream_materialization_id}
            </p>
          )}
        </div>
      )}
    </div>
  );
}

function StatusIcon({ status }: { status: TransformRunStatus }) {
  switch (status) {
    case "succeeded":
      return <CheckCircle className="w-4 h-4 text-green-500 shrink-0" />;
    case "failed":
      return <XCircle className="w-4 h-4 text-destructive shrink-0" />;
    case "running":
    case "pending":
      return <Loader2 className="w-4 h-4 text-muted-foreground animate-spin shrink-0" />;
  }
}

function StatusBadge({ status }: { status: TransformRunStatus }) {
  const variants: Record<TransformRunStatus, string> = {
    succeeded: "bg-green-500/15 text-green-600",
    failed: "bg-destructive/15 text-destructive",
    running: "bg-blue-500/15 text-blue-600",
    pending: "bg-muted text-muted-foreground",
  };

  return (
    <span className={`rounded px-1.5 py-0.5 font-medium capitalize ${variants[status]}`}>
      {status}
    </span>
  );
}

function formatRelative(isoStr: string): string {
  try {
    const diffMs = Date.now() - new Date(isoStr).getTime();
    const diffMin = Math.round(diffMs / 60_000);
    if (diffMin < 1) return "just now";
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.round(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    return new Date(isoStr).toLocaleDateString();
  } catch {
    return isoStr;
  }
}

function formatError(error: string): string {
  try {
    const parsed = JSON.parse(error);
    return JSON.stringify(parsed, null, 2);
  } catch {
    return error;
  }
}

// Re-export for convenience
export type { RunHistoryListProps };

// "Re-run" button — used in expanded rows if we want to add re-run functionality later.
// Kept as a named export for future use.
export function RerunButton({ _transformId }: { _transformId: string }) {
  return (
    <Button variant="outline" size="sm" className="text-xs" disabled>
      Re-run (coming soon)
    </Button>
  );
}
